import { api } from './client';

/** A stored prompt and response.
 *
 *  Mirrors `types.CapturedPayload`. Reading one is audited server-side — the
 *  audit row is written before the body is returned — so a component that
 *  fetches this is putting the operator's name on the record. That is the
 *  whole reason an operator may read a customer's prompt at all, and it is why
 *  nothing here fetches speculatively. */
export interface CapturedPayload {
  id: string;
  estateId: string;
  tenantId: string;
  eventId: string;
  provider: string;
  requestModel: string;
  status: string;
  mode: string;
  request: string;
  response: string;
  truncated?: boolean;
  occurredAt: string;
  expireAt: string;
}

/** One recorded read of one payload. */
export interface PayloadRead {
  id: string;
  requestId: string;
  estateId: string;
  tenantId: string;
  operator: string;
  readAt: string;
}

export type CaptureMode = 'off' | 'errors' | 'sampled' | 'full';

export interface CapturePolicy {
  mode?: CaptureMode;
  sampleRate?: number;
  retentionHours?: number;
}

/** reveal fetches a stored payload AND records the read.
 *
 *  Call it only when an operator has asked. There is no read here that is not
 *  on the record, by design — so a component that pre-fetched "just in case"
 *  would be filling an audit trail with reads nobody performed. */
export function reveal(requestId: string): Promise<CapturedPayload> {
  return api.get<CapturedPayload>(`/api/v1/admin/payloads/${encodeURIComponent(requestId)}`);
}

/** reads lists who has looked at a payload. Not itself audited: looking at the
 *  trail is what the trail is for. */
export async function reads(requestId: string): Promise<PayloadRead[]> {
  const r = await api.get<{ reads: PayloadRead[] }>(
    `/api/v1/admin/payloads/${encodeURIComponent(requestId)}/reads`,
  );
  return r.reads ?? [];
}

/** setCapture changes what an estate stores.
 *
 *  The whole policy every time, never one field: mode, rate and retention only
 *  mean anything together, and a partial update could leave a rate from a
 *  previous decision attached to a new mode. */
export function setCapture(estateId: string, policy: CapturePolicy): Promise<CapturePolicy> {
  return api.put<CapturePolicy>(`/api/v1/admin/estates/${encodeURIComponent(estateId)}/capture`, {
    mode: policy.mode ?? 'off',
    sampleRate: policy.sampleRate ?? 0,
    retentionHours: policy.retentionHours ?? 0,
  });
}
